import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerDescription
} from '@/components/ui/drawer';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle
} from '@/components/ui/sheet';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { formatPrice } from '@/utils/currency';
import { isRtl, t } from '@/utils/i18n';
import { LanguagePicker, detectInitialLang } from '@/components/LanguagePicker';
import { EntertainmentButtons, MiniGamesSheet, AiPlaceholderSheet } from './CustomerEntertainment';
import {
  Bell, MessageSquare, Send, UtensilsCrossed,
  Droplets, ClipboardList, Receipt, AlertCircle, Users, CheckCircle, Menu, Star,
  Plus, Minus, ChevronDown, Gamepad2, Heart, BookOpen, Smile, ArrowLeft, Sparkles
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WS_BASE = process.env.REACT_APP_BACKEND_URL.replace(/^http/, 'ws');

const ICON_MAP = { Bell, Droplets, ClipboardList, Receipt, AlertCircle, Users, CheckCircle, UtensilsCrossed, Menu };
function getIcon(name) { return ICON_MAP[name] || Bell; }

const THEMES = {
  warm: { primary: '#EA580C', primaryLight: '#FFEDD5', bg: '#FAFAF9', accent: '#C2410C' },
  ocean: { primary: '#0284C7', primaryLight: '#E0F2FE', bg: '#F0F9FF', accent: '#0369A1' },
  forest: { primary: '#16A34A', primaryLight: '#DCFCE7', bg: '#F0FDF4', accent: '#15803D' },
  midnight: { primary: '#4F46E5', primaryLight: '#EEF2FF', bg: '#F5F3FF', accent: '#4338CA' },
  rose: { primary: '#E11D48', primaryLight: '#FFE4E6', bg: '#FFF1F2', accent: '#BE123C' },
  amber: { primary: '#D97706', primaryLight: '#FEF3C7', bg: '#FFFBEB', accent: '#B45309' },
};

function RestaurantLogo({ logoPath, theme, size = 80 }) {
  const [errored, setErrored] = useState(false);
  useEffect(() => { setErrored(false); }, [logoPath]);
  if (logoPath && !errored) {
    return (
      <img
        src={`${API}/files/${logoPath}`}
        alt="Restaurant logo"
        onError={() => setErrored(true)}
        className="rounded-2xl object-cover"
        style={{ width: size, height: size }}
      />
    );
  }
  return (
    <div className="rounded-2xl flex items-center justify-center" style={{ width: size, height: size, backgroundColor: theme.primaryLight }}>
      <UtensilsCrossed size={size * 0.4} style={{ color: theme.primary }} />
    </div>
  );
}

export default function CustomerView() {
  const { restaurantId, tableId } = useParams();
  const [restaurant, setRestaurant] = useState(null);
  const [table, setTable] = useState(null);
  const [categories, setCategories] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [callReasons, setCallReasons] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  // Entertainment sheets (one open at a time).
  const [entOpen, setEntOpen] = useState(null); // 'games' | 'quotes' | 'poems' | 'jokes' | null
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [page, setPage] = useState('landing');
  // i18n state — initialized once the restaurant data lands (see effect
  // below) since we need restaurant.default_language. We seed with 'en' so
  // tr() is safe to call on first render before the fetch resolves.
  const [lang, setLangState] = useState('en');
  const tr = useCallback((k, vars) => t(k, lang, vars), [lang]);
  const dir = isRtl(lang) ? 'rtl' : 'ltr';
  const [callSent, setCallSent] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sending, setSending] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [exitDialogOpen, setExitDialogOpen] = useState(false);
  const [ratingValues, setRatingValues] = useState({ service: 0, speed: 0, food: 0, cleanliness: 0 });
  const [submittingRating, setSubmittingRating] = useState(false);
  const [siteSettings, setSiteSettings] = useState({});
  const [cart, setCart] = useState({}); // { [itemId]: qty }
  const [cartReviewOpen, setCartReviewOpen] = useState(false);
  const [orderSubmitting, setOrderSubmitting] = useState(false);
  const [orderConfirmation, setOrderConfirmation] = useState(false); // post-submit success banner
  const [openCats, setOpenCats] = useState(new Set()); // collapsed-by-default; entries here are expanded
  const visibleCatRef = useRef(new Set()); // ids currently in viewport
  const collapseTimers = useRef({}); // { [catId]: timeoutId }
  const wsRef = useRef(null);
  const scrollRef = useRef(null);
  const pollRef = useRef(null);
  const lastReadCountRef = useRef(0);

  const theme = THEMES[restaurant?.theme] || THEMES.warm;

  // ==================== DATA FETCH ====================
  // We fetch everything once on mount in the customer's *default* language
  // (since `lang` state isn't seeded yet on first paint). A second effect
  // below refetches the *translatable* endpoints (menu, call-reasons,
  // restaurant, site-settings) whenever the customer flips languages so
  // on-screen copy swaps to the translated version.
  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [rest, tbl, menuData, reasons, siteS] = await Promise.all([
          axios.get(`${API}/customer/${restaurantId}`),
          axios.get(`${API}/customer/${restaurantId}/table/${tableId}`),
          axios.get(`${API}/customer/${restaurantId}/menu`),
          axios.get(`${API}/customer/${restaurantId}/call-reasons`),
          axios.get(`${API}/site-settings`),
        ]);
        setRestaurant(rest.data);
        setTable(tbl.data);
        setCategories(menuData.data.categories);
        setMenuItems(menuData.data.items);
        setCallReasons(reasons.data);
        setSiteSettings(siteS.data || {});
        // Resolve the customer's language. Auto-detect picks the navigator
        // language when the host has translated into it; otherwise we fall
        // back to the restaurant default. Persisted choice (localStorage)
        // always wins.
        const def = rest.data?.default_language || 'en';
        const available = rest.data?.translation_languages || (rest.data?.secondary_language ? [rest.data.secondary_language] : []);
        const next = detectInitialLang({ restaurantId, defaultLang: def, availableLangs: available });
        setLangState(next);
        // If the seeded language differs from the default we just fetched
        // in, do an immediate refetch of the translatable endpoints so the
        // very first paint already shows the right language.
        if (next !== def) {
          try {
            const q = `?lang=${encodeURIComponent(next)}`;
            const sq = `${q}&restaurant_id=${encodeURIComponent(restaurantId)}`;
            const [r2, m2, cr2, s2] = await Promise.all([
              axios.get(`${API}/customer/${restaurantId}${q}`),
              axios.get(`${API}/customer/${restaurantId}/menu${q}`),
              axios.get(`${API}/customer/${restaurantId}/call-reasons${q}`),
              axios.get(`${API}/site-settings${sq}`),
            ]);
            setRestaurant(r2.data);
            setCategories(m2.data.categories);
            setMenuItems(m2.data.items);
            setCallReasons(cr2.data);
            setSiteSettings(s2.data || {});
          } catch { /* leave default-language data */ }
        }
      } catch {
        setError('Restaurant or table not found');
      }
      setLoading(false);
    };
    fetchAll();
  }, [restaurantId, tableId]);

  // ==================== BACK BUTTON PREVENTION ====================
  const leavingRef = useRef(false);
  useEffect(() => {
    // Push a dummy state so browser back triggers popstate instead of navigating away
    window.history.pushState({ customerView: true }, '');

    const handlePopState = (e) => {
      if (leavingRef.current) return; // Leaving — let the navigation through
      // Push state again to prevent actual navigation
      window.history.pushState({ customerView: true }, '');
      // If on menu page, go back to landing
      if (page === 'menu') {
        setPage('landing');
        return;
      }
      // On landing: open exit dialog only if host enabled either toggle
      const confirmOn = restaurant?.exit_confirm_enabled !== false;
      const ratingOn = !!restaurant?.exit_rating_enabled;
      if (confirmOn || ratingOn) {
        setExitDialogOpen(true);
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [page, restaurant?.exit_confirm_enabled, restaurant?.exit_rating_enabled]);

  // Actually leave the page (breaks out of the SPA history trap)
  const doLeave = () => {
    leavingRef.current = true;
    setExitDialogOpen(false);
    // Hard navigation — fresh page load, our popstate handler is unmounted
    window.location.href = '/goodbye';
  };

  // If the host disables menu visibility while the customer is on the menu page,
  // bounce back to landing.
  useEffect(() => {
    if (page === 'menu' && restaurant?.customer_menu_enabled === false) {
      setPage('landing');
    }
  }, [page, restaurant?.customer_menu_enabled]);

  // ==================== WEBSOCKET ====================
  useEffect(() => {
    if (!restaurantId || !tableId) return;
    let ws;
    let reconnectTimer;
    const connect = () => {
      ws = new WebSocket(`${WS_BASE}/api/ws/customer/${restaurantId}/${tableId}`);
      wsRef.current = ws;
      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);
          if (msg.type === 'new_message' && msg.message.sender_type === 'host') {
            setChatMessages(prev => {
              if (prev.find(m => m.id === msg.message.id)) return prev;
              return [...prev, msg.message];
            });
            // Show toast if chat is not open
            if (!chatOpenRef.current) {
              toast.info(`${msg.message.sender_name}: ${msg.message.content}`, { duration: 4000 });
              setUnreadCount(prev => prev + 1);
            }
          } else if (msg.type === 'new_message' && msg.message.sender_type === 'customer') {
            // Dedup own messages
            setChatMessages(prev => {
              if (prev.find(m => m.id === msg.message.id)) return prev;
              return [...prev, msg.message];
            });
          } else if (msg.type === 'table_reset') {
            setChatMessages([]);
            setUnreadCount(0);
            lastReadCountRef.current = 0;
          }
        } catch {}
      };
      ws.onclose = () => { reconnectTimer = setTimeout(connect, 3000); };
      ws.onerror = () => {};
    };
    connect();
    return () => {
      clearTimeout(reconnectTimer);
      if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) ws.close();
    };
  }, [restaurantId, tableId]);

  // Ref to track chatOpen state inside WS handler
  const chatOpenRef = useRef(false);
  useEffect(() => { chatOpenRef.current = chatOpen; }, [chatOpen]);

  // Load initial chat history
  useEffect(() => {
    if (!restaurantId || !tableId) return;
    axios.get(`${API}/chat/${restaurantId}/${tableId}`).then(r => {
      setChatMessages(r.data);
      lastReadCountRef.current = r.data.length;
    }).catch(() => {});
  }, [restaurantId, tableId]);

  // Always-on polling for chat messages (every 3s)
  useEffect(() => {
    if (!restaurantId || !tableId) return;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/chat/${restaurantId}/${tableId}`);
        setChatMessages(prev => {
          if (data.length === prev.length) return prev;
          // Check for new host messages
          const newHostMsgs = data.filter(m => m.sender_type === 'host' && !prev.find(p => p.id === m.id));
          if (newHostMsgs.length > 0 && !chatOpenRef.current) {
            newHostMsgs.forEach(m => {
              toast.info(`${m.sender_name}: ${m.content}`, { duration: 4000 });
            });
            setUnreadCount(c => c + newHostMsgs.length);
          }
          return data;
        });
      } catch {}
    };
    pollRef.current = setInterval(poll, 3000);
    return () => clearInterval(pollRef.current);
  }, [restaurantId, tableId]);

  // Scroll chat to bottom
  useEffect(() => {
    if (scrollRef.current) {
      const el = scrollRef.current;
      const viewport = el.querySelector('[data-radix-scroll-area-viewport]') || el;
      viewport.scrollTop = viewport.scrollHeight;
    }
  }, [chatMessages.length, chatOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Clear unread when chat opens
  const openChat = useCallback(() => {
    setChatOpen(true);
    setUnreadCount(0);
    lastReadCountRef.current = chatMessages.length;
  }, [chatMessages.length]);

  const handleCall = async (reasonId) => {
    try {
      await axios.post(`${API}/calls/${restaurantId}`, {
        table_id: tableId, reason_id: reasonId, customer_name: customerName || 'Guest'
      });
      setCallSent(true);
      setDrawerOpen(false);
      setTimeout(() => setCallSent(false), 3000);
    } catch {}
  };

  // ==================== CART / ORDER ====================
  const cartCount = Object.values(cart).reduce((a, b) => a + (b || 0), 0);

  // ==================== CATEGORY EXPAND/COLLAPSE ====================
  // Default: all categories collapsed. Tapping a header toggles its expanded
  // state. When the user expands a *new* category, every OTHER currently-
  // expanded category that is OFF-SCREEN at that moment gets armed for
  // auto-collapse `autoCollapseSec` seconds later (per-host setting). The
  // timer is cancelled if the category scrolls back into view, or if the
  // user manually closes it before the timer fires.
  const autoCollapseSec = Math.max(0, Number(restaurant?.menu_auto_collapse_seconds ?? 8) || 0);

  const clearCollapseTimer = (catId) => {
    if (collapseTimers.current[catId]) {
      clearTimeout(collapseTimers.current[catId]);
      delete collapseTimers.current[catId];
    }
  };

  const scheduleAutoCollapse = (catId) => {
    if (autoCollapseSec === 0) return;
    clearCollapseTimer(catId);
    collapseTimers.current[catId] = setTimeout(() => {
      // Only collapse if still off-screen at the moment the timer fires —
      // user may have scrolled back to it during the delay.
      if (!visibleCatRef.current.has(catId)) {
        setOpenCats(prev => { const n = new Set(prev); n.delete(catId); return n; });
      }
      delete collapseTimers.current[catId];
    }, autoCollapseSec * 1000);
  };

  const toggleCat = (catId) => {
    setOpenCats(prev => {
      const next = new Set(prev);
      if (next.has(catId)) {
        // Collapse this one; clear any pending auto-collapse timer for it
        next.delete(catId);
        clearCollapseTimer(catId);
      } else {
        // Expand this one. For every OTHER currently-expanded category
        // that is NOT in the viewport right now, schedule it for auto-
        // collapse so the menu doesn't keep accumulating open sections.
        next.add(catId);
        Array.from(prev).forEach(other => {
          if (other === catId) return;
          if (!visibleCatRef.current.has(other)) {
            scheduleAutoCollapse(other);
          }
        });
      }
      return next;
    });
  };

  // Driven by the IntersectionObserver inside MenuCategorySection. When a
  // category scrolls back into view we cancel any pending collapse timer.
  const onCatVisibilityChange = useCallback((catId, visible) => {
    if (visible) {
      visibleCatRef.current.add(catId);
      clearCollapseTimer(catId);
    } else {
      visibleCatRef.current.delete(catId);
    }
  }, []);

  // Cleanup any pending timers on unmount
  useEffect(() => () => {
    Object.values(collapseTimers.current).forEach(t => clearTimeout(t));
    collapseTimers.current = {};
  }, []);

  const cartLines = Object.entries(cart)
    .map(([itemId, qty]) => {
      const item = menuItems.find(i => i.id === itemId);
      if (!item || !qty) return null;
      return { item_id: itemId, name: item.name, price: Number(item.price) || 0, qty, line_total: (Number(item.price) || 0) * qty };
    })
    .filter(Boolean);
  const cartTotal = cartLines.reduce((sum, l) => sum + l.line_total, 0);

  const setQty = (itemId, qty) => {
    setCart(prev => {
      const next = { ...prev };
      if (qty <= 0) delete next[itemId];
      else next[itemId] = Math.min(99, qty);
      return next;
    });
  };
  const incQty = (itemId) => setQty(itemId, (cart[itemId] || 0) + 1);
  const decQty = (itemId) => setQty(itemId, (cart[itemId] || 0) - 1);
  const clearCart = () => setCart({});

  const submitOrder = async () => {
    if (cartCount === 0 || orderSubmitting) return;
    setOrderSubmitting(true);
    try {
      await axios.post(`${API}/calls/${restaurantId}`, {
        table_id: tableId,
        is_order: true,
        customer_name: customerName || 'Guest',
        order_items: cartLines.map(l => ({ item_id: l.item_id, name: l.name, price: l.price, qty: l.qty })),
      });
      setCartReviewOpen(false);
      clearCart();
      setOrderConfirmation(true);
      setTimeout(() => setOrderConfirmation(false), 4000);
    } catch {
      toast.error('Failed to send order');
    }
    setOrderSubmitting(false);
  };

  const sendChat = async () => {
    if (!chatInput.trim() || sending) return;
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/chat/${restaurantId}/${tableId}`, {
        content: chatInput.trim(), sender_type: 'customer', sender_name: customerName || 'Guest'
      });
      setChatMessages(prev => {
        if (prev.find(m => m.id === data.id)) return prev;
        return [...prev, data];
      });
      setChatInput('');
    } catch {}
    setSending(false);
  };

  const callBtnText = siteSettings.customer_call_button_text || 'Call Server';
  const menuBtnText = siteSettings.customer_menu_button_text || 'View Menu';
  const chatLabel = siteSettings.customer_chat_label || 'Chat with host';
  const welcomeText = siteSettings.customer_welcome_text;

  // ==================== LOADING / ERROR ====================
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: theme.bg }}>
      <div className="w-8 h-8 border-3 rounded-full animate-spin" style={{ borderColor: theme.primary, borderTopColor: 'transparent' }} />
    </div>
  );

  if (error) return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: theme.bg }}>
      <div className="text-center">
        <AlertCircle size={48} className="mx-auto mb-4 text-red-400" />
        <p className="text-lg font-semibold text-stone-700">{error}</p>
      </div>
    </div>
  );

  // Chat icon with unread badge (reused in both pages)
  const ChatIconButton = ({ size: iconSize = 18, className = '' }) => (
    <button
      data-testid="chat-trigger-button"
      onClick={openChat}
      // chat-led-chase = continuous LED-style color cycle on the border & glow.
      // 8 vibrant colors transition one after another in a 2.4s loop, drawing
      // the customer's eye to the chat affordance without being noisy.
      className={`chat-led-chase relative rounded-full border border-stone-200 bg-white p-2 ${className}`}
      aria-label="Open chat with host"
    >
      <MessageSquare size={iconSize} className="text-stone-600" />
      {unreadCount > 0 && (
        <span data-testid="chat-unread-badge" className="absolute -top-1.5 -right-1.5 min-w-[20px] h-5 flex items-center justify-center rounded-full text-[10px] font-bold text-white px-1" style={{ backgroundColor: theme.primary }}>
          {unreadCount > 9 ? '9+' : unreadCount}
        </span>
      )}
    </button>
  );

  // ==================== LANDING PAGE ====================
  const bgPath = restaurant?.customer_bg_image_path;
  const hasBg = !!bgPath;
  // Language toggle helper — persisted per-restaurant so a refresh keeps the
  // selection. We don't auto-detect navigator.language because the host has
  // explicitly chosen what they offer (default + optional secondary).
  const defaultLang   = restaurant?.default_language   || 'en';
  const availableLangs = restaurant?.translation_languages || (restaurant?.secondary_language ? [restaurant.secondary_language] : []);
  const setLang = (code) => {
    setLangState(code);
    try { localStorage.setItem(`cust_lang:${restaurantId}`, code); } catch { /* ignore */ }
    // Re-pull the translatable endpoints in the chosen language. The static
    // i18n dictionary handles button labels & status banners synchronously;
    // these calls swap host-typed menu items, call-reason labels, welcome /
    // paging messages, and the admin-defined big button labels (See Menu /
    // Call Us / Welcome banner).
    const q  = `?lang=${encodeURIComponent(code)}`;
    const sq = `${q}&restaurant_id=${encodeURIComponent(restaurantId)}`;
    Promise.all([
      axios.get(`${API}/customer/${restaurantId}${q}`),
      axios.get(`${API}/customer/${restaurantId}/menu${q}`),
      axios.get(`${API}/customer/${restaurantId}/call-reasons${q}`),
      axios.get(`${API}/site-settings${sq}`),
    ])
      .then(([r, m, cr, s]) => {
        setRestaurant(r.data);
        setCategories(m.data.categories);
        setMenuItems(m.data.items);
        setCallReasons(cr.data);
        setSiteSettings(s.data || {});
      })
      .catch(() => { /* keep previous data on failure */ });
  };
  // Floating bottom-right pill + bottom-sheet wheel picker. Rendered once
  // per page so it appears on every customer screen (landing + menu).
  const LangFloatingPicker = (
    <LanguagePicker
      currentLang={lang}
      defaultLang={defaultLang}
      availableLangs={availableLangs}
      onChange={setLang}
      accent={theme.primary}
    />
  );
  const landingStyle = hasBg
    ? {
        backgroundColor: theme.bg,
        backgroundImage: `url(${API}/files/${bgPath})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      }
    : { backgroundColor: theme.bg };
  if (page === 'landing') return (
    <div className="min-h-screen flex flex-col relative" style={landingStyle} data-testid="customer-landing-root" dir={dir}>
      {LangFloatingPicker}
      {hasBg && (
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none"
          style={{
            // Soft vertical gradient: image stays clearly visible in the middle band,
            // edges fade to theme.bg so the heading and CTA buttons remain readable
            // regardless of the photo's brightness.
            background: `linear-gradient(180deg, ${theme.bg}E6 0%, ${theme.bg}66 35%, ${theme.bg}66 65%, ${theme.bg}F2 100%)`,
          }}
        />
      )}
      <div className="relative flex-1 flex flex-col items-center px-6 pt-12 pb-8">
        <div className="animate-fade-in-up mb-6">
          <RestaurantLogo logoPath={restaurant?.logo_path} theme={theme} size={96} />
        </div>
        <div className="text-center animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
          <h1 className="text-3xl sm:text-4xl font-bold text-stone-900 tracking-tight" style={{ fontFamily: 'Outfit' }}>{restaurant?.name}</h1>
          {restaurant?.description && <p className="text-sm text-stone-500 mt-2 max-w-xs mx-auto">{restaurant.description}</p>}
          <div className="mt-3 inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium" style={{ backgroundColor: theme.primaryLight, color: theme.primary }}>{table?.label}</div>
        </div>
        {welcomeText && (
          <p className="text-sm text-stone-600 mt-4 text-center max-w-xs mx-auto italic">{welcomeText}</p>
        )}
        <div className="w-full max-w-xs mt-8 animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
          <Input data-testid="customer-name-input" value={customerName} onChange={e => setCustomerName(e.target.value)} placeholder={tr('your_name')} className="text-center h-12 rounded-xl border-stone-200 bg-white" />
        </div>
        <div className="w-full max-w-xs mt-6 space-y-3 animate-fade-in-up" style={{ animationDelay: '0.3s' }}>
          {restaurant?.customer_menu_enabled !== false && (
            <Button data-testid="customer-enter-btn" onClick={() => setPage('menu')} className="w-full h-14 text-base font-bold text-white rounded-2xl gap-3" style={{ backgroundColor: theme.primary, boxShadow: `0 6px 24px ${theme.primary}40` }}>
              <Menu size={22} /> {menuBtnText}
            </Button>
          )}
          <button data-testid="customer-call-button-landing" onClick={() => setDrawerOpen(true)} className="w-full h-14 rounded-2xl font-bold text-base flex items-center justify-center gap-3 border-2 transition-all active:scale-[0.97]" style={{ borderColor: theme.primary, color: theme.primary, backgroundColor: theme.primaryLight }}>
            <Bell size={22} /> {callBtnText}
          </button>
        </div>
        {callSent && (
          <div className="mt-4 p-3 rounded-xl text-center text-sm font-medium text-white animate-fade-in-up w-full max-w-xs" style={{ backgroundColor: theme.primary }}>{tr('host_notified')}</div>
        )}
        <div className="mt-6">
          <ChatIconButton size={20} className="shadow-sm" />
        </div>
        {/* 4 entertainment buttons below the chat icon — only those whose effective
            flag is true (package allows + host enabled) actually render. */}
        <EntertainmentButtons enabled={restaurant?.entertainment} onOpen={setEntOpen} tr={tr} />
        {/* Powered-by footer — sits below the entertainment buttons, always visible
            inside the landing viewport. Auto-margin pushes it to the bottom when
            content is shorter than the screen. */}
        <div className="mt-auto pt-8 pb-2 text-center" data-testid="customer-powered-by">
          <p className="text-xs text-stone-600">
            {tr('powered_by')}{' '}
            <a
              href="https://olafel.com"
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold underline underline-offset-2 hover:opacity-80 transition-opacity"
              style={{ color: theme.primary }}
              data-testid="customer-powered-by-link"
            >
              Olafel.com
            </a>
          </p>
        </div>
      </div>

      <CallReasonsDrawer drawerOpen={drawerOpen} setDrawerOpen={setDrawerOpen} callReasons={callReasons} handleCall={handleCall} theme={theme} tr={tr} dir={dir} />
      <ChatSheet chatOpen={chatOpen} setChatOpen={setChatOpen} chatMessages={chatMessages} chatInput={chatInput} setChatInput={setChatInput} sendChat={sendChat} sending={sending} scrollRef={scrollRef} theme={theme} tr={tr} dir={dir} />
      <MiniGamesSheet open={entOpen === 'games'} onOpenChange={(o) => !o && setEntOpen(null)} />
      <AiPlaceholderSheet open={entOpen === 'quotes'} onOpenChange={(o) => !o && setEntOpen(null)} kind="quotes" restaurantId={restaurantId} />
      <AiPlaceholderSheet open={entOpen === 'poems'}  onOpenChange={(o) => !o && setEntOpen(null)} kind="poems"  restaurantId={restaurantId} />
      <AiPlaceholderSheet open={entOpen === 'jokes'}  onOpenChange={(o) => !o && setEntOpen(null)} kind="jokes"  restaurantId={restaurantId} />

      {/* Exit confirmation / rating dialog */}
      <AlertDialog open={exitDialogOpen} onOpenChange={setExitDialogOpen}>
        <AlertDialogContent className="max-w-sm max-h-[90vh] overflow-y-auto">
          <AlertDialogHeader>
            <AlertDialogTitle style={{ fontFamily: 'Outfit' }}>
              {restaurant?.exit_rating_enabled ? 'Rate Your Experience' : 'Leave this page?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {restaurant?.exit_rating_enabled
                ? "Tell us how we did. You'll need to scan the QR again to return."
                : "You'll need to scan the QR again to return."
              }
            </AlertDialogDescription>
          </AlertDialogHeader>

          {restaurant?.exit_rating_enabled && (
            <div data-testid="exit-rating-form" className="space-y-3 py-2">
              {[
                { key: 'service', label: 'Service' },
                { key: 'speed', label: 'Speed' },
                { key: 'food', label: 'Food' },
                { key: 'cleanliness', label: 'Cleanliness' },
              ].map(row => (
                <div key={row.key} className="flex items-center justify-between gap-3">
                  <span className="text-sm font-medium text-stone-700">{row.label}</span>
                  <div className="flex gap-1">
                    {[1, 2, 3, 4, 5].map(star => (
                      <button
                        key={star}
                        data-testid={`star-${row.key}-${star}`}
                        type="button"
                        onClick={() => setRatingValues(p => ({ ...p, [row.key]: p[row.key] === star ? 0 : star }))}
                        className="p-1 -m-1 transition-transform active:scale-110"
                      >
                        <Star
                          size={24}
                          className={ratingValues[row.key] >= star ? 'fill-yellow-400 text-yellow-400' : 'text-stone-300'}
                        />
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          <AlertDialogFooter className="flex-col gap-2 sm:flex-col">
            {restaurant?.exit_rating_enabled ? (
              <Button
                data-testid="submit-rating-btn"
                disabled={submittingRating || !Object.values(ratingValues).some(v => v > 0)}
                onClick={async () => {
                  try {
                    setSubmittingRating(true);
                    const payload = { table_id: tableId };
                    Object.entries(ratingValues).forEach(([k, v]) => { if (v > 0) payload[k] = v; });
                    await axios.post(`${API}/ratings/${restaurantId}`, payload);
                    toast.success('Thanks for your feedback!');
                    setRatingValues({ service: 0, speed: 0, food: 0, cleanliness: 0 });
                    setExitDialogOpen(false);
                    doLeave();
                  } catch { toast.error('Could not submit rating'); }
                  finally { setSubmittingRating(false); }
                }}
                style={{ backgroundColor: theme.primary }}
                className="w-full text-white"
              >
                {submittingRating ? 'Submitting…' : 'Submit & Leave'}
              </Button>
            ) : (
              <AlertDialogAction data-testid="exit-confirm-btn" onClick={doLeave} className="w-full bg-red-600 hover:bg-red-700 text-white">Leave</AlertDialogAction>
            )}
            <AlertDialogCancel data-testid="exit-cancel-btn" className="w-full mt-0">Stay</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );

  // ==================== MENU PAGE ====================
  // If host disabled the menu, force the customer back to the landing screen.
  if (page === 'menu' && restaurant?.customer_menu_enabled === false) {
    return null;
  }
  return (
    <div className="w-full max-w-md mx-auto min-h-screen relative pb-36" style={{ backgroundColor: theme.bg }} dir={dir}>
      {LangFloatingPicker}
      <div className="sticky top-0 z-40 backdrop-blur-xl border-b px-4 py-3" style={{ backgroundColor: `${theme.bg}E6`, borderColor: `${theme.primary}20` }}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => setPage('landing')} className="text-stone-400 hover:text-stone-600 transition-colors" data-testid="back-to-landing-btn">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m15 18-6-6 6-6"/></svg>
            </button>
            {restaurant?.logo_path && <RestaurantLogo logoPath={restaurant.logo_path} theme={theme} size={32} />}
            <div>
              <h1 className="text-base font-bold text-stone-900" style={{ fontFamily: 'Outfit' }}>{restaurant?.name}</h1>
              <p className="text-[11px] text-stone-500">{table?.label} &middot; {customerName || 'Guest'}</p>
            </div>
          </div>
          <ChatIconButton />
        </div>
      </div>

      <div className="p-4 space-y-3 pb-32">
        {categories.map(cat => {
          const items = menuItems.filter(i => i.category_id === cat.id);
          if (items.length === 0) return null;
          const isOpen = openCats.has(cat.id);
          return (
            <MenuCategorySection
              key={cat.id}
              cat={cat}
              isOpen={isOpen}
              onToggle={() => toggleCat(cat.id)}
              onVisibilityChange={onCatVisibilityChange}
              theme={theme}
            >
              <div className="space-y-3 mt-3">
                {items.map(item => (
                  <MenuItemCard
                    key={item.id}
                    item={item}
                    theme={theme}
                    currency={restaurant?.currency || 'USD'}
                    qty={cart[item.id] || 0}
                    onInc={() => incQty(item.id)}
                    onDec={() => decQty(item.id)}
                    soldOutLabel={tr('sold_out')}
                  />
                ))}
              </div>
            </MenuCategorySection>
          );
        })}
        {categories.length === 0 && (
          <div className="text-center py-12"><Menu size={40} className="mx-auto text-stone-300 mb-3" /><p className="text-stone-500">{tr('menu_preparing')}</p></div>
        )}
      </div>

      <div className="fixed bottom-0 left-0 right-0 max-w-md mx-auto p-4 z-50" style={{ background: `linear-gradient(to top, ${theme.bg}, ${theme.bg}, transparent)` }}>
        {orderConfirmation && (
          <div className="mb-3 p-3 rounded-xl text-center text-sm font-medium text-white animate-fade-in-up" style={{ backgroundColor: theme.primary }}>
            {tr('order_sent')}
          </div>
        )}
        {callSent && !orderConfirmation && <div className="mb-3 p-3 rounded-xl text-center text-sm font-medium text-white animate-fade-in-up" style={{ backgroundColor: theme.primary }}>{tr('host_notified')}</div>}
        {cartCount > 0 && (
          <button
            type="button"
            data-testid="review-order-bar"
            onClick={() => setCartReviewOpen(true)}
            className="w-full mb-2 rounded-2xl text-white font-semibold text-base py-3.5 flex items-center justify-between px-4 active:scale-[.98] transition-transform"
            style={{ backgroundColor: theme.primary, boxShadow: `0 6px 22px ${theme.primary}55` }}
          >
            <span className="flex items-center gap-2">
              <ClipboardList size={18} />
              {tr('review_order')}
              <span className="ml-1 text-xs px-2 py-0.5 rounded-full bg-white/25">{cartCount} {tr('items_count')}</span>
            </span>
            <span className="text-sm font-bold">{formatPrice(cartTotal, restaurant?.currency || 'USD')}</span>
          </button>
        )}
        <button data-testid="customer-call-button" onClick={() => setDrawerOpen(true)} className="w-full rounded-2xl text-white font-bold text-lg py-5 flex items-center justify-center gap-3 active:scale-95 transition-transform" style={{ backgroundColor: theme.primary, boxShadow: `0 8px 32px ${theme.primary}66` }}>
          <Bell size={24} /> {callBtnText}
        </button>
      </div>

      <CallReasonsDrawer drawerOpen={drawerOpen} setDrawerOpen={setDrawerOpen} callReasons={callReasons} handleCall={handleCall} theme={theme} tr={tr} dir={dir} />
      <ChatSheet chatOpen={chatOpen} setChatOpen={setChatOpen} chatMessages={chatMessages} chatInput={chatInput} setChatInput={setChatInput} sendChat={sendChat} sending={sending} scrollRef={scrollRef} theme={theme} tr={tr} dir={dir} />

      {/* Order review dialog */}
      <AlertDialog open={cartReviewOpen} onOpenChange={setCartReviewOpen}>
        <AlertDialogContent data-testid="cart-review-dialog" className="max-w-sm max-h-[90vh] overflow-y-auto">
          <AlertDialogHeader>
            <AlertDialogTitle style={{ fontFamily: 'Outfit' }}>Confirm your order</AlertDialogTitle>
            <AlertDialogDescription>{table?.label ? `For ${table.label}` : ''} — please review before sending</AlertDialogDescription>
          </AlertDialogHeader>
          <div className="max-h-72 overflow-y-auto space-y-2 py-2">
            {cartLines.map(l => (
              <div key={l.item_id} className="flex items-center justify-between gap-3 text-sm">
                <span className="flex-1 truncate">
                  <span className="font-medium text-stone-800">{l.qty} ×</span>{' '}
                  <span className="text-stone-700">{l.name}</span>
                </span>
                <span className="text-stone-600 font-mono">{formatPrice(l.line_total, restaurant?.currency || 'USD')}</span>
              </div>
            ))}
          </div>
          <div className="border-t border-stone-200 pt-3 flex items-center justify-between">
            <span className="text-sm font-semibold text-stone-700">Total</span>
            <span className="text-base font-bold" style={{ color: theme.primary }}>{formatPrice(cartTotal, restaurant?.currency || 'USD')}</span>
          </div>
          <AlertDialogFooter className="mt-2">
            <AlertDialogCancel data-testid="cart-review-cancel">Keep editing</AlertDialogCancel>
            <button
              type="button"
              data-testid="cart-review-submit"
              onClick={submitOrder}
              disabled={orderSubmitting}
              className="inline-flex items-center justify-center gap-2 rounded-md px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
              style={{ backgroundColor: theme.primary }}
            >
              {orderSubmitting ? 'Sending…' : 'Confirm & Send'}
            </button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {/* Exit confirmation dialog (for browser back on menu page handled by popstate -> goes to landing) */}
      <AlertDialog open={exitDialogOpen} onOpenChange={setExitDialogOpen}>
        <AlertDialogContent className="max-h-[90vh] overflow-y-auto">
          <AlertDialogHeader>
            <AlertDialogTitle style={{ fontFamily: 'Outfit' }}>Leave this page?</AlertDialogTitle>
            <AlertDialogDescription>You'll need to scan the QR code again to return.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="exit-cancel-btn">Stay</AlertDialogCancel>
            <AlertDialogAction data-testid="exit-confirm-btn" onClick={() => { window.history.go(-(window.history.length - 1)); }} className="bg-red-600 hover:bg-red-700 text-white">Leave</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ==================== SHARED: Call Reasons Drawer ====================
function CallReasonsDrawer({ drawerOpen, setDrawerOpen, callReasons, handleCall, theme, tr, dir }) {
  return (
    <Drawer open={drawerOpen} onOpenChange={setDrawerOpen}>
      <DrawerContent data-testid="call-reason-drawer" className="rounded-t-3xl" dir={dir}>
        <DrawerHeader className="text-center">
          <DrawerTitle style={{ fontFamily: 'Outfit' }}>{tr('how_can_we_help')}</DrawerTitle>
          <DrawerDescription>&nbsp;</DrawerDescription>
        </DrawerHeader>
        <div className="p-6 pt-2 flex flex-col gap-3 pb-8">
          {callReasons.map(reason => {
            const Icon = getIcon(reason.icon);
            return (
              <button key={reason.id} data-testid="call-reason-item" onClick={() => handleCall(reason.id)} className="flex items-center gap-4 h-14 px-5 rounded-xl border-2 border-stone-200 hover:border-stone-300 bg-white font-medium text-stone-800 transition-all active:scale-[0.98]">
                <Icon size={22} style={{ color: theme.primary }} />
                <span className="text-base">{reason.label}</span>
              </button>
            );
          })}
        </div>
      </DrawerContent>
    </Drawer>
  );
}

// ==================== SHARED: Chat Sheet ====================
function ChatSheet({ chatOpen, setChatOpen, chatMessages, chatInput, setChatInput, sendChat, sending, scrollRef, theme, tr, dir }) {
  return (
    <Sheet open={chatOpen} onOpenChange={setChatOpen}>
      <SheetContent side="bottom" className="h-[80vh] flex flex-col p-0 rounded-t-3xl" dir={dir}>
        <SheetHeader className="p-4 pb-2 border-b border-stone-100">
          <SheetTitle style={{ fontFamily: 'Outfit' }}>{tr('chat_with_host')}</SheetTitle>
        </SheetHeader>
        <ScrollArea className="flex-1 p-4" ref={scrollRef}>
          <div className="space-y-3">
            {chatMessages.map(m => (
              <div key={m.id} className={`flex ${m.sender_type === 'customer' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[75%] px-4 py-2.5 rounded-2xl text-sm ${m.sender_type === 'customer' ? 'text-white rounded-br-md' : 'bg-stone-100 text-stone-800 rounded-bl-md'}`} style={m.sender_type === 'customer' ? { backgroundColor: theme.primary } : {}}>
                  <p className="text-xs font-medium opacity-70 mb-0.5">{m.sender_name}</p>
                  <p>{m.content}</p>
                </div>
              </div>
            ))}
            {chatMessages.length === 0 && <p className="text-center text-stone-400 text-sm py-8">&nbsp;</p>}
          </div>
        </ScrollArea>
        <div className="p-3 border-t border-stone-100 flex gap-2">
          <Input data-testid="chat-message-input" value={chatInput} onChange={e => setChatInput(e.target.value)} placeholder={tr('type_a_message')} className="flex-1" onKeyDown={e => e.key === 'Enter' && sendChat()} />
          <Button data-testid="chat-send-button" onClick={sendChat} disabled={sending} className="text-white px-4" style={{ backgroundColor: theme.primary }}><Send size={16} /></Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

// ==================== Menu Item Card ====================
function MenuItemCard({ item, theme, currency, qty = 0, onInc, onDec, soldOutLabel }) {
  const [imgSrc, setImgSrc] = useState(null);
  const isAvailable = item.available !== false;
  const isSelected = qty > 0;
  useEffect(() => {
    if (!item.image_path) return;
    let revoke;
    axios.get(`${API}/files/${item.image_path}`, { responseType: 'blob' })
      .then(r => { const url = URL.createObjectURL(r.data); revoke = url; setImgSrc(url); })
      .catch(() => {});
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, [item.image_path]);
  return (
    <div
      data-testid={`menu-item-${item.id}`}
      className={`flex gap-4 p-3 rounded-2xl border shadow-sm items-center transition-all ${!isAvailable ? 'opacity-50 grayscale border-stone-100 bg-white' : isSelected ? 'border-2' : 'border-stone-100 bg-white'}`}
      style={isSelected && isAvailable ? { backgroundColor: `${theme.primary}1A`, borderColor: `${theme.primary}66` } : {}}
    >
      {imgSrc ? <img src={imgSrc} alt={item.name} className="w-20 h-20 rounded-xl object-cover flex-shrink-0" /> : (
        <div className="w-20 h-20 rounded-xl flex-shrink-0 flex items-center justify-center" style={{ backgroundColor: theme.primaryLight }}><UtensilsCrossed size={24} style={{ color: theme.primary }} /></div>
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="font-semibold text-stone-900 text-sm">{item.name}</p>
          {!isAvailable && <Badge variant="outline" className="text-[10px] border-stone-300 text-stone-500">{soldOutLabel || 'Sold out'}</Badge>}
        </div>
        {item.description && <p className="text-xs text-stone-500 mt-0.5 line-clamp-2">{item.description}</p>}
        <div className="flex items-center justify-between mt-1.5 gap-2">
          <p className="text-sm font-bold" style={{ color: theme.primary }}>{formatPrice(item.price, currency || 'USD')}</p>
          {isAvailable && (
            qty === 0 ? (
              <button
                type="button"
                data-testid={`add-to-cart-${item.id}`}
                onClick={onInc}
                className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold text-white active:scale-95 transition-transform"
                style={{ backgroundColor: theme.primary }}
                aria-label="Add to order"
              >
                <Plus size={12} /> Add
              </button>
            ) : (
              <div className="flex items-center gap-2 rounded-full px-1 py-0.5" style={{ backgroundColor: `${theme.primary}26` }}>
                <button
                  type="button"
                  data-testid={`decrement-${item.id}`}
                  onClick={onDec}
                  className="w-7 h-7 rounded-full flex items-center justify-center text-white active:scale-90 transition-transform"
                  style={{ backgroundColor: theme.primary }}
                  aria-label="Decrease quantity"
                >
                  <Minus size={12} />
                </button>
                <span className="min-w-5 text-center text-sm font-bold" style={{ color: theme.primary }}>{qty}</span>
                <button
                  type="button"
                  data-testid={`increment-${item.id}`}
                  onClick={onInc}
                  className="w-7 h-7 rounded-full flex items-center justify-center text-white active:scale-90 transition-transform"
                  style={{ backgroundColor: theme.primary }}
                  aria-label="Increase quantity"
                >
                  <Plus size={12} />
                </button>
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
}

function CategoryImage({ path }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    if (!path) return;
    let revoke;
    axios.get(`${API}/files/${path}`, { responseType: 'blob' })
      .then(r => { const url = URL.createObjectURL(r.data); revoke = url; setSrc(url); })
      .catch(() => {});
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, [path]);
  if (!src) return <div className="w-12 h-12 rounded-xl bg-stone-100" />;
  return <img src={src} alt="Category" className="w-12 h-12 rounded-xl object-cover" />;
}

// ==================== Collapsible Category Section ====================
function MenuCategorySection({ cat, isOpen, onToggle, onVisibilityChange, theme, children }) {
  // The IntersectionObserver tracks the *header button* — not the whole
  // section wrapper — because expanded sections often span more height
  // than the viewport. Observing the wrapper means the items below the
  // header keep the section "intersecting" long after the user has
  // scrolled past it, defeating the auto-collapse logic. The header is a
  // small, predictable anchor that gives a clean visible/not-visible
  // signal matching the customer's mental model of "this section's label
  // is on screen."
  const headerRef = useRef(null);
  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return;
    const el = headerRef.current;
    if (!el) return;
    const obs = new IntersectionObserver((entries) => {
      // Multiple threshold crossings may be delivered together — only the
      // last entry represents the current state.
      const e = entries[entries.length - 1];
      if (!e) return;
      onVisibilityChange(cat.id, e.isIntersecting);
    }, { threshold: 0, rootMargin: '0px 0px -10% 0px' });
    obs.observe(el);
    return () => obs.disconnect();
  }, [onVisibilityChange, cat.id]);

  return (
    <div data-testid={`customer-cat-${cat.id}`} className="rounded-2xl bg-white border border-stone-100 shadow-sm overflow-hidden">
      <button
        ref={headerRef}
        type="button"
        data-testid={`customer-cat-toggle-${cat.id}`}
        onClick={onToggle}
        className="w-full flex items-center gap-3 p-3 text-left hover:bg-stone-50 active:bg-stone-100 transition-colors"
        aria-expanded={isOpen}
      >
        {cat.image_path ? (
          <CategoryImage path={cat.image_path} />
        ) : (
          <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: theme.primaryLight }}>
            <Menu size={18} style={{ color: theme.primary }} />
          </div>
        )}
        <div className="min-w-0 flex-1">
          <h2 className="text-base font-bold text-stone-800 leading-tight truncate" style={{ fontFamily: 'Outfit' }}>{cat.name}</h2>
          {cat.description && <p className="text-xs text-stone-500 italic line-clamp-1">{cat.description}</p>}
        </div>
        <ChevronDown
          size={20}
          className="text-stone-400 transition-transform duration-200 shrink-0"
          style={{ transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}
        />
      </button>
      {isOpen && <div className="px-3 pb-3">{children}</div>}
    </div>
  );
}
